<template>
  <b-modal
    id="duplicateDeliverableDetailModal"
    modal-class="edit-deliverable-modal-pop"
    size="xl"
    hide-footer
    title="Deliverable Preview"
    body-class="p-0"
  >
    <div v-if="deliverable" class="edit-deliverable-modal">
      <!-- Departments wrapper -->
      <div class="departments__wrapper">
        <div class="data-items department-title">
          <span>{{ deliverable.template_title || 'Project Deliverable' }}</span>
        </div>
        <div v-if="hasDepartments" class="deliverable-projects">
          <div class="pb-0 titles exclude-outside-click">
            <div class="department-title exclude-outside-click">
              <span
                v-for="(dept, i) in deliverable.departments"
                :key="'dup-prev-dept-' + dept.department_id"
              >{{ dept.name }}<span v-if="i < deliverable.departments.length - 1">, </span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Title + Tags row -->
      <div class="row">
        <div class="col-md-5">
          <label class="form-group w-100">
            <h1 class="exclude-outside-click edit-d-title deliverable-title">{{ deliverable.title }}</h1>
          </label>
        </div>
        <div class="col-md-7">
          <div class="form-group">
            <div class="tags__wrapper">
              <div class="deliverable-list-tags">
                <span class="text-uppercase field-label">Tags</span>
                <div class="info-bubbles cursor-pointer exclude-outside-click">
                  <span
                    v-for="tag in deliverable.tags"
                    :key="'dup-prev-tag-' + tag.tag_id"
                    class="exclude-outside-click badge badge-gray deliverable-tags"
                  >{{ tag.name }}</span>
                  <span v-if="!hasTags" class="text-muted">—</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main content row: Description (left) + Dates/Contact/Resources (right) -->
      <div class="row">
        <div class="col-md-12">
          <div class="row">

            <!-- Left: Description -->
            <div class="col-md-8">
              <div class="row no-gutters">
                <div class="col-md-12">
                  <div class="field-box">
                    <div class="field-container--notes_research">
                      <div class="field-header">
                        <div class="field-label">Deliverable Description</div>
                      </div>
                      <div class="field-wrap">
                        <div
                          v-if="deliverable.description"
                          class="dup-preview-description"
                          v-html="deliverable.description"
                        ></div>
                        <span v-else class="text-muted">—</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Dates, Coordinator, Contact, Resources -->
            <div class="col-md-4">
              <div class="row no-gutters">

                <!-- Start Date -->
                <div class="col-md-6" style="padding-right: 10px;">
                  <div class="field-box">
                    <div class="field-container--start_date">
                      <div class="field-header">
                        <div class="field-label">Start Date</div>
                      </div>
                      <div class="field-wrap">
                        <span class="exclude-outside-click field-value text-hover-bg">
                          <span class="text-muted">No Date</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- End Date -->
                <div class="col-md-6" style="padding-left: 10px;">
                  <div class="field-box">
                    <div class="field-container--end_date">
                      <div class="field-header">
                        <div class="field-label">End Date</div>
                      </div>
                      <div class="field-wrap">
                        <span class="exclude-outside-click field-value text-hover-bg">
                          <span class="text-muted">No Date</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Coordinator -->
                <div class="col-md-12" v-if="deliverable.coordinator_name">
                  <div class="field-box">
                    <div class="field-header">
                      <div class="field-label">Coordinator</div>
                    </div>
                    <div class="field-wrap">
                      <span class="field-value text-hover-bg">{{ deliverable.coordinator_name }}</span>
                    </div>
                  </div>
                </div>

                <!-- Contact / Outlet -->
                <div class="col-md-12">
                  <div class="field-box">
                    <div class="field-box__header">
                      <div class="add-contact-wrapper">
                        <button class="add-contact-button exclude-outside-click d-inline-block" disabled>Who</button>
                      </div>
                    </div>
                    <div v-if="hasOutlets">
                      <div
                        v-for="outlet in deliverable.outlets"
                        :key="'dup-prev-outlet-' + outlet.contact_id"
                        class="contact-box"
                      >
                        <div class="contact-box__name">{{ outlet.contact_name }}</div>
                        <div v-if="outlet.position" class="contact-box__position">{{ outlet.position }}</div>
                        <div v-if="outlet.company_name" class="contact-box__company_name">{{ outlet.company_name }}</div>
                        <div v-if="outlet.phone" class="contact-box__phone"><a :href="'tel:' + outlet.phone">T: {{ outlet.phone }}</a></div>
                        <div v-if="outlet.address" class="contact-box__address">{{ outlet.address }}</div>
                        <div v-if="outlet.city" class="contact-box__city">{{ outlet.city }}</div>
                        <div v-if="outlet.province" class="contact-box__province">{{ outlet.province }}</div>
                        <div v-if="outlet.country_id" class="contact-box__country_id">{{ outlet.country_id }}</div>
                        <div v-if="outlet.postal_code" class="contact-box__postal_code">{{ outlet.postal_code }}</div>
                        <div v-if="outlet.region" class="contact-box__region">{{ outlet.region }}</div>
                      </div>
                    </div>
                    <span v-else class="text-muted d-block pb-2">—</span>
                  </div>
                </div>

                <!-- Resources -->
                <div class="col-md-12 field-box">
                  <div class="field-box__header">
                    <div class="dropdown">
                      <button class="add-resource-button exclude-outside-click d-inline-block" disabled>Resources</button>
                    </div>
                  </div>
                  <div class="field-box__content">
                    <div v-if="hasResources">
                      <div
                        v-for="(resource, i) in deliverable.resources"
                        :key="'dup-prev-res-' + i"
                        class="resource-box"
                      >
                        <div class="media">
                          <div class="media-body">
                            <div class="resource-box__url"></div>
                            <a
                              v-if="resource.url"
                              :href="resource.url"
                              target="_blank"
                              rel="noopener noreferrer"
                            >{{ resource.label || resource.url }}</a>
                            <span v-else>{{ resource.label }}</span>
                            <div v-if="resource.notes" class="resource-box__notes">{{ resource.notes }}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <span v-else class="text-muted">—</span>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div id="task__field__labels">
            <div class="field-block d-flex flex-row">
              <div class="d-flex flex-row sortable--fields">
                <div class="field-control-wrapper field-control-wrapper__title no-radius no-draggable-field">
                  <div class="task-title">
                    <div class="az task-table-header__task">
                      Tasks <span class="task-count-second">({{ taskCount }})</span>
                    </div>
                  </div>
                </div>
                <div class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__user">
                  <div class="task-table-header"><div class="field-label"><span>Assigned to</span></div></div>
                </div>
                <div v-if="false" class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__due_date">
                  <div class="task-table-header"><div class="field-label"><span>Due Date</span></div></div>
                </div>
                <div class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__status">
                  <div class="task-table-header"><div class="field-label"><span>Status</span></div></div>
                </div>
                <div class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__status_note">
                  <div class="task-table-header"><div class="field-label"><span>Notes</span></div></div>
                </div>
              </div>
            </div>
          </div>

          <div class="task-fields">
            <template v-if="hasTasks">
              <div
                v-for="task in previewTasks"
                :key="'dup-prev-task-' + task.id"
                class="d-flex flex-row field-block"
              >
                <div class="field-control-wrapper field-control-wrapper__title no-radius">
                  <div class="task-title">
                    <div class="field-wrap">
                      <span class="field-value text-hover-bg">{{ task.title }}</span>
                    </div>
                  </div>
                </div>
                <div class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__user">
                  <div class="custom-title">
                    <div class="field-wrap field-wrap-users no-icon">
                      <div class="field-value">
                        <span v-if="task.user_name" class="info-bubbles">
                          <span class="initials badge badge-primary" :title="task.user_name">{{ task.user_initials || initialsFromName(task.user_name) }}</span>
                        </span>
                        <span v-else class="text-muted">Unassigned</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-if="false" class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__due_date">
                  <div class="custom-title">
                    <div class="field-wrap field-wrap-datepicker">
                      <span class="field-value text-hover-bg">No Deadline</span>
                    </div>
                  </div>
                </div>
                <div class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__status">
                  <div class="custom-title">
                    <div class="field-wrap no-icon">
                      <span class="field-value status-field-value" :style="{ backgroundColor: task.status_color || '#778899' }">{{ task.status_name || 'Not Started' }}</span>
                    </div>
                  </div>
                </div>
                <div class="d-none d-lg-block field-rows field-control-wrapper field-control-wrapper__status_note">
                  <div class="custom-title">
                    <div class="field-wrap">
                      <span class="field-value text-hover-bg pt-20">{{ task.notes || '—' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <div v-else class="p-3 text-muted">No tasks</div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: "DuplicateDeliverableDetailModal",
  props: {
    deliverable: {
      type: Object,
      default: null,
    },
  },
  computed: {
    hasDepartments() {
      return this.deliverable && Array.isArray(this.deliverable.departments) && this.deliverable.departments.length > 0;
    },
    hasTags() {
      return this.deliverable && Array.isArray(this.deliverable.tags) && this.deliverable.tags.length > 0;
    },
    hasOutlets() {
      return this.deliverable && Array.isArray(this.deliverable.outlets) && this.deliverable.outlets.length > 0;
    },
    hasTasks() {
      return this.deliverable && Array.isArray(this.deliverable.tasks) && this.deliverable.tasks.length > 0;
    },
    previewTasks() {
      if (!this.hasTasks) return [];
      return this.deliverable.tasks.map(task => ({ ...task, due_date: null }));
    },
    hasResources() {
      return this.deliverable && Array.isArray(this.deliverable.resources) && this.deliverable.resources.length > 0;
    },
    taskCount() {
      if (!this.deliverable) return 0;
      if (typeof this.deliverable.task_count === "number") return this.deliverable.task_count;
      return this.hasTasks ? this.deliverable.tasks.length : 0;
    },
  },
  methods: {
    formatDate(value) {
      if (!value) return "-";
      try {
        const d = new Date(value);
        if (isNaN(d.getTime())) return value;
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const day = String(d.getDate()).padStart(2, "0");
        return `${day} ${months[d.getMonth()]}, ${d.getFullYear()}`;
      } catch (e) {
        return value;
      }
    },
    initialsFromName(name) {
      if (!name) return "";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0] ? parts[0].charAt(0) : "";
      const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
      return (first + last).toUpperCase();
    },
  },
};
</script>

<style>
#duplicateDeliverableDetailModal___BV_modal_content_ {
  padding: 16px;
}

.edit-deliverable-modal-pop .modal-dialog {
  max-width: 90vw;
}

.edit-deliverable-modal-pop .modal-body {
  padding: 24px 28px;
}

.dup-preview-description {
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 4px;
  background: #fafafa;
}
</style>
